import { Service, inject } from '@angular/core';
import { assertExists } from 'common';
import {
  Search,
  SearchOptions,
  SearchPrompt,
  SearchProvider,
  SearchResult,
  SearchResultResponse,
  SearchResults,
  Searches,
  createApiSearchPromptString,
} from 'models';
import { NGXLogger } from 'ngx-logger';
import {
  Observable,
  ReplaySubject,
  filter,
  finalize,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { SEARCH_PROVIDER } from '../common/tokens';
import { SearchesQuery } from '../state/searches/searches.query';

const getKey = (
  databaseId: number,
  archiveId: number,
  searchId: number,
  searchCriteria: string,
  tabId: number,
  includeExtendedData: boolean,
  page: number,
  pageSize: number,
  sort: string
) =>
  `${databaseId}-${archiveId}-${searchId}-${searchCriteria}-${tabId}-${includeExtendedData ? 'ext' : 'std'}-${page}-${pageSize}-${sort && sort.trim() ? sort : 'NOSORT'}`;

interface CachedSearchResultResponse {
  /** Datetime the request was made and cached. */
  retrieved: Date;
  options: SearchOptions;
  /** A subject containing the results for the search. */
  resultResponse: ReplaySubject<SearchResultResponse>;
}

const ttlMs = 0;

const isExpired = (retrieved: Date, ttlMs: number): boolean => {
  if (ttlMs <= 0) return false; // treat 0/negative as "never expire"
  return Date.now() - retrieved.getTime() >= ttlMs;
};

@Service()
export class SearchResultCacheService implements SearchProvider {
  private logger = inject(NGXLogger);
  private searchProvider = inject(SEARCH_PROVIDER);
  private searchQuery = inject(SearchesQuery);
  private cache = new Map<string, CachedSearchResultResponse>();
  private inFlightRequests = new Map<
    string,
    Observable<SearchResultResponse>
  >();

  private databaseId?: number;
  private archiveId?: number;
  private searchId?: number;
  private promptString?: string;

  constructor() {
    this.searchQuery.searchRouteParams$
      .pipe(
        filter(
          // Ensure the active route does contain a search.
          ([databaseId, archiveId, searchId]) => !!databaseId && !!searchId
        )
      )
      .subscribe(([databaseId, archiveId, searchId, searchPrompts]) => {
        assertExists(databaseId);
        assertExists(searchId);
        if (!this.databaseId) {
          // Set initial active database.
          this.databaseId = databaseId;
        }

        if (!this.searchId) {
          // Set initial active search id
          this.searchId = searchId;
        }

        const promptString = createApiSearchPromptString(searchPrompts);

        if (!this.promptString) {
          // Set initial active prompt;
          this.promptString = createApiSearchPromptString(searchPrompts);
        }

        if (
          this.databaseId !== databaseId ||
          this.searchId !== searchId ||
          this.promptString !== promptString
        ) {
          // Database id or search id was previously another value
          // Clear cache and in-flight requests
          for (const key of this.cache.keys()) {
            const keyParts = key.split('-');
            if (
              Number(keyParts[0]) === this.databaseId &&
              Number(keyParts[2]) === this.searchId &&
              keyParts[3] === this.promptString
            ) {
              this.cache.delete(key);
            }
          }
          for (const key of this.inFlightRequests.keys()) {
            const keyParts = key.split('-');
            if (
              Number(keyParts[0]) === this.databaseId &&
              Number(keyParts[2]) === this.searchId &&
              keyParts[3] === this.promptString
            ) {
              this.inFlightRequests.delete(key);
            }
          }
        }

        this.databaseId = databaseId;
        this.archiveId = archiveId; // todo I don't think I need this one
        this.searchId = searchId;
        this.promptString = promptString;
      });
  }

  /**
   * Clears the cache of any entry with the provided db, archive, search id and optionally promps.
   * Also clears any in-flight requests for the same keys.
   *
   * @param databaseId Database id.
   * @param archiveId Archive id.
   * @param searchId Search id.
   * @param prompts The search prompts for the search. This can be either an array of {@link SearchPrompt} or the prompt string create using {@link createApiSearchPromptString}.
   */
  deleteCacheResult(
    databaseId: number,
    archiveId: number,
    searchId: number,
    prompts?: string | SearchPrompt[]
  ) {
    let startsWithKey = `${databaseId}-${archiveId}-${searchId}`;
    if (prompts) {
      const promptString =
        typeof prompts === 'string'
          ? prompts
          : createApiSearchPromptString(prompts);
      // Add prompt to key
      startsWithKey += `-${promptString}`;
    }
    for (const key of this.cache.keys()) {
      if (key.startsWith(startsWithKey)) {
        this.cache.delete(key);
      }
    }
    for (const key of this.inFlightRequests.keys()) {
      if (key.startsWith(startsWithKey)) {
        this.inFlightRequests.delete(key);
      }
    }
  }

  /** @inheritdoc */
  getAll(databaseId: number, archiveId?: number): Observable<Searches> {
    return this.searchProvider.getAll(databaseId, archiveId);
  }

  /** @inheritdoc */
  getDocumentData(
    databaseId: number,
    archiveId: number,
    documentId: number,
    secureId: string
  ): Observable<SearchResult> {
    return this.searchProvider.getDocumentData(
      databaseId,
      archiveId,
      documentId,
      secureId
    );
  }

  /** @inheritdoc */
  getTableData(
    databaseId: number,
    archiveId: number,
    documentId: number,
    secureId: string
  ): Observable<SearchResults> {
    return this.searchProvider.getTableData(
      databaseId,
      archiveId,
      documentId,
      secureId
    );
  }

  /**
   * Execute a search to get count for an archive with caching.
   * If the request matches an unexpired cached result the cached result is returned. Otherwise a query is made to the API and cached.
   * All API requests are made with extended data enabled to avoid redundant calls.
   * All cache lookups use the extended data key since that's what we always store.
   *
   * @param databaseId Database id.
   * @param search Search.
   * @param options Search options (includeExtendedData will be forced to true for actual API calls).
   * @returns An observable search result response.
   */
  run(
    databaseId: number,
    search: Search,
    options: SearchOptions
  ): Observable<SearchResultResponse> {
    const requestingCountOnly = options.countOnly ?? false;

    // Always use extended data key since we always fetch with extended data
    const cacheKey = getKey(
      databaseId,
      options.targetArchiveId,
      search.id,
      options.searchCriteria,
      options.tabId,
      true, // Always true since we force includeExtendedData for all API calls
      options.page,
      options.recordsPerPage,
      options.sort
    );

    // Check cache first
    const cachedResultResponse = this.cache.get(cacheKey);

    // For count-only requests, pagination doesn't matter - any cached result will have the count
    const paginationMatches =
      requestingCountOnly ||
      (cachedResultResponse &&
        cachedResultResponse.options.page === options.page &&
        cachedResultResponse.options.recordsPerPage ===
          options.recordsPerPage &&
        cachedResultResponse.options.sort ===
          cachedResultResponse.options.sort);

    if (
      cachedResultResponse &&
      !isExpired(cachedResultResponse.retrieved, ttlMs) &&
      paginationMatches
    ) {
      this.logger.debug(
        requestingCountOnly
          ? 'Returning cached result for count request'
          : 'Returning cached result with matching pagination'
      );
      return cachedResultResponse.resultResponse.asObservable();
    }

    // Check for in-flight requests
    const inFlightRequest = this.inFlightRequests.get(cacheKey);

    if (inFlightRequest) {
      this.logger.debug('Sharing in-flight request for same search');
      return inFlightRequest;
    }

    // Getting here means there is no cached search response, it is expired, or no in-flight request.
    // To avoid redundancy, always fetch with extended data and full results
    options.countOnly = false;
    options.includeExtendedData = true;

    const request$ = this.searchProvider.run(databaseId, search, options).pipe(
      switchMap((searchResponse) => {
        const replaySubject = new ReplaySubject<SearchResultResponse>(1);
        replaySubject.next(searchResponse);
        replaySubject.complete();
        this.cache.set(cacheKey, {
          retrieved: new Date(),
          options,
          resultResponse: replaySubject,
        });

        return replaySubject.asObservable();
      }),
      tap((searchResponse) => {
        this.logger.debug('Search cache emitted a response: ', searchResponse);
      }),
      finalize(() => {
        // Clean up in-flight tracking when request completes
        this.inFlightRequests.delete(cacheKey);
      }),
      // Share the observable so multiple subscribers don't trigger multiple HTTP requests
      shareReplay(1)
    );

    // Track this request as in-flight using the extended data key
    this.inFlightRequests.set(cacheKey, request$);

    return request$;
  }

  /** @inheritdoc */
  runForCount(
    databaseId: number,
    search: Search,
    options: SearchOptions
  ): Observable<number> {
    return this.searchProvider.runForCount(databaseId, search, options);
  }
}
